import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, Zap, Server, Activity, Globe, GitBranch, ExternalLink,
} from 'lucide-react';
import type { AppInfo, ServerInfo, ServerStatus } from '../deploy/types';
import { STATUS_COLOR, serverFlag } from '../deploy/deployWidgets';

interface DashboardPageProps {
  servers: ServerInfo[];
  apps: AppInfo[];
  serverStatus: Record<string, { status: ServerStatus; info: string }>;
}

export default function DashboardPage({ servers, apps, serverStatus }: DashboardPageProps) {
  const online = servers.filter((s) => serverStatus[s.id]?.status === 'online').length;
  const offline = servers.filter((s) => serverStatus[s.id]?.status === 'offline').length;

  const hubs = [
    { to: '/deploy', icon: <Zap size={18} />, title: 'Deploy', desc: 'Build, deploy, rollback, nginx, and logs per app and server.' },
    { to: '/servers', icon: <Server size={18} />, title: 'Servers', desc: 'Metrics, journal/file logs, diagnostics, and maintenance.' },
    { to: '/services', icon: <Activity size={18} />, title: 'Services', desc: 'systemd catalog, start/stop, and managed unit files.' },
    { to: '/dns', icon: <Globe size={18} />, title: 'DNS / BIND', desc: 'Zone files and named.conf.local over SSH.' },
  ] as const;

  return (
    <div className="dash-overview">
      <div className="dns-shell dash-shell dash-overview-shell">
        <main className="main dash-main dash-overview-main">
          <div className="dash-overview-hero card">
            <div className="dash-overview-hero-icon">
              <LayoutDashboard size={28} />
            </div>
            <div>
              <h2 className="dash-overview-h2">Dashboard</h2>
              <p className="dash-overview-lead">
                MapNexa deployment control — quick view of infrastructure and shortcuts to each area.
              </p>
            </div>
          </div>

          <div className="dash-stat-grid">
            <div className="dash-stat card">
              <span className="dash-stat-label">Servers</span>
              <span className="dash-stat-value">{servers.length}</span>
              <span className="dash-stat-meta">
                {online > 0 && <span className="srv-status-tag online">{online} online</span>}
                {offline > 0 && <span className="srv-status-tag offline">{offline} offline</span>}
                {servers.length > 0 && online === 0 && offline === 0 && <span className="dash-stat-muted">Test connections from Deploy or Config</span>}
              </span>
            </div>
            <div className="dash-stat card">
              <span className="dash-stat-label">Applications</span>
              <span className="dash-stat-value">{apps.length}</span>
              <span className="dash-stat-meta">Configured for deploy</span>
            </div>
          </div>

          <div className="dash-hub-cards">
            {hubs.map((h) => (
              <NavLink key={h.to} to={h.to} className="dash-hub-card card">
                <div className="dash-hub-card-icon">{h.icon}</div>
                <div>
                  <div className="dash-hub-card-title">{h.title}</div>
                  <p className="dash-hub-card-desc">{h.desc}</p>
                </div>
              </NavLink>
            ))}
          </div>

          <section className="dash-section card">
            <div className="card-title">Servers</div>
            <div className="dash-table-wrap">
              <table className="dash-table">
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Host</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {servers.length === 0 && (
                    <tr>
                      <td colSpan={3} className="dash-table-empty">
                        No servers yet — open Config to add servers.
                      </td>
                    </tr>
                  )}
                  {servers.map((s) => {
                    const st = serverStatus[s.id]?.status ?? 'unknown';
                    return (
                      <tr key={s.id}>
                        <td>
                          <span className="srv-flag">{serverFlag(s.label)}</span> {s.label}
                        </td>
                        <td>
                          <code className="dash-code">{s.username}@{s.host}:{s.port}</code>
                        </td>
                        <td>
                          <span className="dash-dot" style={{ background: STATUS_COLOR[st] }} />
                          {st === 'online' && <span className="srv-status-tag online">Online</span>}
                          {st === 'offline' && <span className="srv-status-tag offline">Offline</span>}
                          {st === 'testing' && <span className="srv-status-tag">Testing</span>}
                          {st === 'unknown' && <span className="dash-stat-muted">Unknown</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <section className="dash-section card">
            <div className="card-title">Applications</div>
            <div className="dash-table-wrap">
              <table className="dash-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>URL / path</th>
                    <th>Branch</th>
                  </tr>
                </thead>
                <tbody>
                  {apps.length === 0 && (
                    <tr>
                      <td colSpan={3} className="dash-table-empty">
                        No apps yet — open Config to add applications.
                      </td>
                    </tr>
                  )}
                  {apps.map((a) => (
                    <tr key={a.id}>
                      <td>{a.name}</td>
                      <td>
                        {a.url ? (
                          <a href={`https://${a.url}`} target="_blank" rel="noreferrer" className="dash-app-link">
                            {a.url} <ExternalLink size={11} />
                          </a>
                        ) : (
                          <code className="dash-code">{a.sourceDir}</code>
                        )}
                      </td>
                      <td>{a.gitBranch ? <span className="tag-branch"><GitBranch size={10} />{a.gitBranch}</span> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
